import { gql, useMutation, useQuery } from "@apollo/client";
import {
  GET_ALL_USER_NOTIFICATIONS,
  GET_PAYMENT_CARDS_PERSONAL,
  GET_USER_CALENDAR_INVITATIONS,
  MARK_NOTIFICATION_AS_READ,
  SWITCH_COMPANY,
  ME,
  GET_CURRENT_COMPANY,
  GET_USER_RATINGS,
  GET_EVENT_RATINGS,
} from "../queries/users";
import { FAMILY_MEMBER_IN_USER } from "../queries/users";

export function useFamilyMembersInUser(userID: string | null, skip = false) {
  const { data, loading, error, refetch } = useQuery<{
    familyMemberInUser: any;
  }>(FAMILY_MEMBER_IN_USER, {
    variables: { userID },
    skip: skip || !userID,
  });

  return {
    data: data?.familyMemberInUser ?? [],
    loading,
    error,
    refetch,
  };
};

export const usePaymentMethods = () => {
  const { data, loading, error } = useQuery<{
    getPaymentCardsPersonal: any;
  }>(GET_PAYMENT_CARDS_PERSONAL);

  return {
    data: data?.getPaymentCardsPersonal ?? [],
    loading,
    error,
  };
};

export const useAllUserNotifications = (eventId: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getAllUserNotifications: any;
  }>(GET_ALL_USER_NOTIFICATIONS, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    data: data?.getAllUserNotifications ?? [],
    loading,
    error,
    refetch,
  };
};

export function useMarkNotificationAsRead() {
  const [markAsReadMutation, { loading, error }] = useMutation(
    MARK_NOTIFICATION_AS_READ,
  );

  const markAsRead = async (notificationId: string) => {
    return markAsReadMutation({
      variables: { data: { notificationId } },
    });
  };

  return { markAsRead, loading, error };
}

const EMPTY_CALENDAR_INVITATIONS: any[] = [];

export const useUserCalendarInvitations = (
  data: {
    userId: string;
    companyMembershipIds: string[];
    employeeIds?: string[];
  },
  options?: { skip?: boolean },
) => {
  const {
    data: queryData,
    loading,
    error,
    refetch,
  } = useQuery<{
    getUserCalendarInvitations: any;
  }>(GET_USER_CALENDAR_INVITATIONS, {
    variables: { data },
    skip:
      options?.skip ||
      !data.userId ||
      (data.employeeIds !== undefined && !data.employeeIds.length),
  });

  return {
    data: queryData?.getUserCalendarInvitations ?? EMPTY_CALENDAR_INVITATIONS,
    loading,
    error,
    refetch,
  };
};

export function useSwitchCompany() {
  const [switchCompanyMutation, { loading, error }] =
    useMutation(SWITCH_COMPANY);

  const switchCompany = async (data: { id: string; eventID?: string; personalProfile?: boolean }) => {
    return switchCompanyMutation({
      variables: { data },
      errorPolicy: "none",
    });
  };

  return { switchCompany, loading, error };
}

export const useMe = () => {
  const { data, loading, error, refetch } = useQuery<{
    me: any;
  }>(ME);

  return {
    data: data?.me ?? null,
    loading,
    error,
    refetch,
  };
};

export const useGetCompanyCards = (companyId?: string) => {
  const arg = companyId ? `(companyId: "${companyId}")` : "";
  const query = gql`
    {
      getPaymentCards${arg}
    }
  `;

  const { data, loading, error, refetch } = useQuery<{
    getPaymentCards: any;
  }>(query);

  return {
    companyCards: data?.getPaymentCards ?? [],
    loading,
    error,
    refetch,
  };
};

export const useCurrentCompany = () => {
  const { data, loading, error, refetch } = useQuery<{
    getCurrentCompany: any;
  }>(GET_CURRENT_COMPANY);

  return {
    simpleCurrentCompany: data?.getCurrentCompany ?? null,
    loading,
    error,
    refetch,
  };
};

export const useUserRatings = (eventId: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getUserRatings: any;
  }>(GET_USER_RATINGS, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    userRatings: data?.getUserRatings ?? [],
    loading,
    error,
    refetch,
  };
};

export const useEventRatings = (eventId: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getEventRatings: any;
  }>(GET_EVENT_RATINGS, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    eventRatings: data?.getEventRatings ?? [],
    loading,
    error,
    refetch,
  };
};
